import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Params } from '@angular/router';
import { defer, finalize, forkJoin, mergeMap, Subject, takeUntil, tap } from 'rxjs';
import { FabrikatDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/fabrikat-dto';
import { ModelDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/model-dto';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { AuditHistoryEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/audit-history-enum';
import { StamdataStatusEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/stamdata-status-enum';
import { GetModellerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-modeller-request';
import { FabrikatService, ModelService, ProduktService } from 'src/app/api/services';
import { EditModelDialogData, ModelFilter } from 'src/app/components/aftale/models';
import { HistoryViewComponent } from 'src/app/components/shared/history/history-view/history-view.component';
import { IHistoryView } from 'src/app/components/shared/history/history.model';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { AuthService } from 'src/app/services/auth.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { StamdataStatusService } from '../../shared/stamdata-status.service';
import { ModelEditDialogComponent } from '../model-edit-dialog/model-edit-dialog.component';

@Component({
  selector: 'app-model-list',
  templateUrl: './model-list.component.html',
  styleUrls: ['./model-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModelListComponent implements OnInit {
  fabrikater: FabrikatDto[] = [];
  filteredFabrikater: FabrikatDto[] = [];
  produkter: ProduktDto[] = [];
  modeller: ModelDto[] = [];
  @ViewChild(MatTable) table: MatTable<any>;
  displayedColumns: string[] = ['fabrikat', 'produkt', 'name', 'modelstatus', 'actions'];

  filter: ModelFilter = {
    productIds: ([] = []),
    fabrikatIds: ([] = []),
  };

  unsubscribe$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private modelService: ModelService,
    private auth: AuthService,
    private statusservice: StamdataStatusService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private fabrikatService: FabrikatService,
    private productService: ProduktService,
  ) {}

  get canEdit() {
    return this.auth.isAdministrator;
  }

  ngOnInit(): void {
    //this.init();

    forkJoin([
      this.fabrikatService.getFabrikater({ caller: EGIS_SERVICE_CALLER }),
      this.productService.getProdukter({
        caller: EGIS_SERVICE_CALLER,
      }),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (results: [FabrikatDto[], ProduktDto[]]) => {
          this.fabrikater = results[0];
          this.produkter = results[1];
          this.filterFabrikater();
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.route.queryParams
      .pipe(
        mergeMap((queryParams: Params) => {
          const filter: ModelFilter = {
            productIds: ([] = []),
            fabrikatIds: ([] = []),
          };

          const productIds = queryParams['productIds'];

          if (productIds) {
            if (productIds instanceof Array) {
              filter.productIds = productIds;
            } else {
              filter.productIds.push(productIds);
            }
          }

          filter.productIds = filter.productIds.filter((v, i, a) => a.indexOf(v) === i);

          this.filterFabrikater();

          const fabrikatIds = queryParams['fabrikatIds'];

          if (fabrikatIds) {
            if (fabrikatIds instanceof Array) {
              filter.fabrikatIds = fabrikatIds;
            } else {
              filter.fabrikatIds.push(fabrikatIds);
            }
          }

          filter.fabrikatIds = filter.fabrikatIds.filter((v, i, a) => a.indexOf(v) === i);

          this.filter = filter;

          return this.getModeller$;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (result: ModelDto[]) => {},
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
          this.modeller = [];
          //this.dataSource = new MatTableDataSource(this.modeller);
        },
      );
  }

  filterFabrikater() {
    const fabrikaterByProduct =
      this.filter.productIds.length > 0 ? this.fabrikater.filter((x) => this.filter.productIds.includes(x.produktId)) : this.fabrikater;
    this.filteredFabrikater = fabrikaterByProduct;
  }

  getModeller$ = defer(() => {
    this.loader.show();

    const request: GetModellerRequest = {
      productIds: this.filter.productIds,
      fabrikatIds: this.filter.fabrikatIds,
    };

    return this.modelService.getModeller({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(
      tap((response) => {
        this.modeller = response;
      }),
      finalize(() => {
        this.loader.hide();
      }),
      takeUntil(this.unsubscribe$),
    );
  });

  edit(row: ModelDto) {
    const data: EditModelDialogData = {
      fabrikater: this.fabrikater,
      model: row,
      fabrikat: row.fabrikat,
    };

    let dialogRef = this.dialog.open(ModelEditDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((p) => {
      if (p) {
        const reload = this.historyView;
        Object.assign(row, p);
        if (reload) this.showhistory(row);
        this.historyViewComponent.render();

        this.table.renderRows();
      }
    });
  }

  add() {
    let fabrikat = this.fabrikater.find((x) => x.id == this.filter.fabrikatIds[0]);
    let produkt = this.produkter.find((x) => x.id == this.filter.productIds[0]);

    const data: EditModelDialogData = {
      fabrikater: this.fabrikater,
      fabrikat: fabrikat,
      produkt: produkt,
    };

    let dialogRef = this.dialog.open(ModelEditDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((p) => {
      if (p) {
        p.forEach((x) => {
          this.modeller.push(x);
        });

        this.historyView = null;
        this.table.renderRows();
      }
    });
  }

  modelstatus(modelStatus: StamdataStatusEnum) {
    return this.statusservice.stamdatastatus(modelStatus);
  }

  @ViewChild(HistoryViewComponent) historyViewComponent: HistoryViewComponent;
  historyView: IHistoryView;
  showhistory(row: ModelDto) {
    this.historyView = { entityId: row.id, historyType: AuditHistoryEnum.Model, title: `${row.name}` };
  }
}
